import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { interval, Subscription, fromEvent, merge, Subject, of } from 'rxjs';
import { debounceTime, takeUntil, filter, switchMap, catchError } from 'rxjs/operators';
import { TokenService } from './token';
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  // Cada 30s verificamos estado (sin refrescar por defecto)
  private readonly SESSION_CHECK_INTERVAL = 30000;
  private readonly INACTIVITY_TIMEOUT = environment.session_timeout * 60 * 1000;
  private readonly FORCED_REFRESH_PROBE_INTERVAL = 1 * 60 * 1000; // 1 minutos
  private sessionCheckSubscription: Subscription | null = null;
  private activitySubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private inactivityTimer: any;
  private destroy$ = new Subject<void>();

  private _isSessionClosed = false;
  private lastUrl: string = '/';

  // Timestamps para control
  private lastRefreshProbeAt = 0;
  private lastActivityAt = Date.now();

  // Limpieza de listener de Hub
  private hubUnsubscribe?: () => void;

  constructor(
    private router: Router,
    private tokenService: TokenService
  ) {
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        if (!event.url.includes('sessionExpired')) {
          this.lastUrl = event.url;
        }
      });

    // Escuchar eventos de Auth (Amplify v6)
    this.hubUnsubscribe = Hub.listen('auth', ({ payload }) => {
      const evt = payload?.event;
      // Cuando Amplify falla renovando tokens o se recibe un signOut, cerramos sesiÃ³n
      if (evt === 'tokenRefresh_failure' || evt === 'signedOut') {
        console.log(`🔔 Hub auth event recibido: ${evt}`);
        this.handleInvalidSession();
      }
    });
  }

  get isSessionClosed(): boolean {
    return this._isSessionClosed;
  }

												
  public getMonitoringStatus() {
    return {
      sessionCheckActive: !!this.sessionCheckSubscription,
      activityMonitoringActive: !!this.activitySubscription,
      inactivityTimerActive: !!this.inactivityTimer,
      isSessionClosed: this._isSessionClosed,
      inactivityTimeout: this.INACTIVITY_TIMEOUT,
      sessionCheckInterval: this.SESSION_CHECK_INTERVAL,
      forcedRefreshProbeInterval: this.FORCED_REFRESH_PROBE_INTERVAL
    };
  }

  startMonitoring() {
    console.log('🔄 Iniciando monitoreo de sesiÃ³n...');
	
    this.stopMonitoring();

    this._isSessionClosed = false;
    this.lastRefreshProbeAt = 0;
    this.lastActivityAt = Date.now();

    this.startSessionChecks();
    this.startInactivityMonitoring();
  }

  stopMonitoring() {
    console.log('🛑 Deteniendo monitoreo de sesiÃ³n...');

    if (this.sessionCheckSubscription) {
      this.sessionCheckSubscription.unsubscribe();
      this.sessionCheckSubscription = null;
    }

    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }

    this.clearInactivityTimer();
  }

  dispose() {
    this.stopMonitoring();

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }

    if (this.hubUnsubscribe) {
      this.hubUnsubscribe();
      this.hubUnsubscribe = undefined;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verificaciones periÃ³dicas de estado de sesiÃ³n (sin refrescar tokens por defecto)
   */
  private startSessionChecks() {
    console.log(`🔍Iniciando verificaciones de sesiÃ³n cada ${this.SESSION_CHECK_INTERVAL / 1000} segundos...`);

    this.sessionCheckSubscription = interval(this.SESSION_CHECK_INTERVAL)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.performSessionCheck()),
        catchError((error) => {
          console.error('❌ Error en verificaciÃ³n de sesiÃ³n:', error);
          this.handleInvalidSession();
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * VerificaciÃ³n completa de sesiÃ³n:
   * - Usuario actual
   * - Tokens presentes y no expirados
   * - Sonda espaciada para detectar revocaciÃ³n del refresh token
   */
  private async performSessionCheck(): Promise<boolean> {
    if (this._isSessionClosed) return false;    
    try {
      console.log('🔍Verificando estado de sesiÃ³n...');                  
      const userOk = await this.ensureCurrentUser();
      if (!userOk) return false;

      const authSession = await this.fetchAuthSessionSafe();
      if (!authSession) return false;

      const idToken = this.getIdTokenOrInvalidate(authSession.tokens);
      if (!idToken) return false;

      const currentTime = Date.now();
      if (this.isTokenExpired(idToken, currentTime)) return false;

      const probeOk = await this.runRefreshProbeIfNeeded(currentTime);
      if (!probeOk) return false;

      console.log('✅ Sesión válida');
      return true;
    } catch (error: any) {
      console.error('❌ Error general en verificación de sesión:', error);
                                                                             
      if (this.isSessionInvalidError(error)) {
        console.log('🚨 Sesión invalidada detectada por error específico');
        this.handleInvalidSession();
      }
      return false;
    }
  }

  private async ensureCurrentUser(): Promise<boolean> {
    try {
      const currentUser = await getCurrentUser();
      console.log('✅ Usuario actual encontrado:', currentUser.username);
      return true;
    } catch (userError) {
      console.error('❌ No se pudo obtener usuario actual:', userError);
      this.handleInvalidSession();
      return false;
    }
  }

  private async fetchAuthSessionSafe() {
    try {
      const authSession = await fetchAuthSession({ forceRefresh: false });
      console.log('🔍 Sesión de auth obtenida');
      return authSession;
    } catch (sessionError) {
      console.error('❌ Error obteniendo sesión:', sessionError);
      this.handleInvalidSession();
      return null;
    }
  }

  private getIdTokenOrInvalidate(tokens: any): any | null {
    if (!tokens || !tokens.accessToken || !tokens.idToken) {
      console.error('❌ Tokens faltantes en la sesión');
      this.handleInvalidSession();
      return null;
    }
    return tokens.idToken;
  }

  private isTokenExpired(idToken: any, currentTime: number): boolean {
    const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
    const expTime = payload.exp * 1000;
    console.log('⏰ Token expira en: ', new Date(expTime).toISOString());
    console.log('⏰ Tiempo actual: ', new Date(currentTime).toISOString());
    if (currentTime >= expTime) {
      console.error('❌ Token ya expirado');
      this.handleInvalidSession();
      return true;
    }
    return false;
  }

  private async runRefreshProbeIfNeeded(currentTime: number): Promise<boolean> {
    console.log(
      '⏰ Diferencia: ',
      currentTime - this.lastRefreshProbeAt,
      '>=',
      this.FORCED_REFRESH_PROBE_INTERVAL,
      'ms desde última sonda de revocación'
    );

    if ((currentTime - this.lastRefreshProbeAt) < this.FORCED_REFRESH_PROBE_INTERVAL) {
      return true;
    }

    console.log('🔄 Ejecutando sonda de revocación (forceRefresh) ...');
    try {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      const refreshedTokens = refreshedSession.tokens;
      if (refreshedTokens && refreshedTokens.idToken) {
        this.tokenService.tokenGG = refreshedTokens.idToken.toString();
        console.log('✅ Sonda OK: refresh token válidos');
      } else {
        console.error('❌ Sonda: no se obtuvieron tokens al forzar refresh');
      }
      return true;
    } catch (refreshError: any) {
      console.error('❌ Sonda falló (posible revocación):', refreshError?.name || refreshError?.message || refreshError);
      if (this.isRefreshRevokedError(refreshError)) {
        console.log('🚨 Revocación detectada al forzar refresh');
        this.handleInvalidSession();
        return false;
      }
      return true;
    } finally {
      this.lastRefreshProbeAt = currentTime;
    }
  }

  private isRefreshRevokedError(refreshError: any): boolean {
    return (
      refreshError?.name === 'NotAuthorizedException' ||
      refreshError?.message?.includes('Refresh Token has been revoked') ||
      refreshError?.message?.includes('invalid_grant') ||
      refreshError?.message?.includes('User is not authenticated')
    );
  }

  private isSessionInvalidError(error: any): boolean {
    return (
      error?.message?.includes('User is not authenticated') ||
      error?.message?.includes('No current user') ||
      error?.message?.includes('NotAuthorizedException') ||
      error?.message?.includes('TokenExpiredError') ||
      error?.message?.includes('Refresh Token has been revoked')
    );
  }

  // Monitoreo de inactividad
  private startInactivityMonitoring() {
    console.log(`⏱️ Iniciando monitoreo de inactividad (timeout: ${this.INACTIVITY_TIMEOUT / 60000} minutos)...`);
    const events = ['mousemove', 'keypress', 'keydown', 'click', 'scroll', 'touchstart', 'touchmove'];
    const userActivity = merge(
      ...events.map(event => fromEvent(document, event))
    ).pipe(
      debounceTime(1000),
      takeUntil(this.destroy$)
    );

    this.activitySubscription = userActivity.subscribe((event: any) => {
      if (!this._isSessionClosed) {
        this.lastActivityAt = Date.now();
        console.log('👆 Actividad detectada:', event.type, 'reseteando timer...');
        this.resetInactivityTimer();
      }
    });

    this.resetInactivityTimer();
							
    console.log('📊 Estado del monitoreo:', this.getMonitoringStatus());
  }

  private resetInactivityTimer() {
    this.clearInactivityTimer();
    console.log(`⏰ Timer de inactividad iniciado: ${this.INACTIVITY_TIMEOUT / 60000} minutos`);
    this.inactivityTimer = setTimeout(() => {
      console.log(`⏰ Usuario inactivo por ${this.INACTIVITY_TIMEOUT / 60000} minutos, cerrando sesión...`);
      this.handleInactiveSession();
    }, this.INACTIVITY_TIMEOUT);
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      console.log('🧹 Limpiando timer de inactividad...');
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private async handleInactiveSession() {
    if (this._isSessionClosed) {
      console.log('⚠️ Sesión ya cerrada, ignorando manejo de inactividad...');
      return;
    }
    console.log('🚨 Cerrando sesión por inactividad...');
    this._isSessionClosed = true;
    this.stopMonitoring();

    try {
      await signOut({ global: true });
      localStorage.setItem('session_status', 'inactivity');
      alert('Su sesión fue cerrada debido a inactividad.');
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: 'true', reason: 'inactivity' }
      });
    } catch (error) {
      console.error('❌ Error cerrando sesión por inactividad:', error);
      localStorage.setItem('session_status', 'inactivity');
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: 'true', reason: 'inactivity' }
      });
    }
  }

															   
	 
  private async handleInvalidSession() {
    if (this._isSessionClosed) {
      console.log('⚠️ Sesión ya cerrada, ignorando manejo de sesión inválida...');
      return;
    }
    console.log('🚨 Detectada sesión inválida, cerrando...');
    this._isSessionClosed = true;
    this.stopMonitoring();

    try {
      this.tokenService.clearToken();
																		   
      try {
        await signOut({ global: true });
        console.log('✅ Sesión cerrada exitosamente en AWS');
      } catch (signOutError) {
        console.log('❌ Error cerrando sesión en AWS (probablemente ya cerrada):', signOutError);
      }
      localStorage.setItem('session_status', 'expired');
      alert('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: 'true', reason: 'expired' }
      });
    } catch (error) {
      console.error('❌ Error en manejo de sesión inválida:', error);
      localStorage.setItem('session_status', 'expired');
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: 'true', reason: 'expired' }
      });
    }
  }

  async logout() {
    if (this._isSessionClosed) {
      console.log('⚠️ Sesión ya cerrada, ignorando logout manual...');
      return;
    }
	
    console.log('🚪 Cerrando sesión manualmente...');
	
    this._isSessionClosed = true;
    this.stopMonitoring();

    try {
      await signOut({ global: true });
      localStorage.removeItem('session_status');
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('❌ Error en logout manual:', error);
      localStorage.removeItem('session_status');
      this.router.navigate(['/login']);
    }
  }
}
